import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * IPD admission sticker — printed once a patient is admitted. Same physical
 * size as the OPD sticker (100mm × 50mm) so the receptionist can use the
 * same Zebra / TSC label printer; layout is denser because IPD has more
 * fields to surface on the patient's bed-side file:
 *
 *   • IPD no.           — branch-scoped IPD-YYYY-####
 *   • UHID              — patient ID
 *   • Name + Age/Sex    — quick clinical identity
 *   • Admit date / time
 *   • Ward / Bed
 *   • Payer / Panel     — useful for ward staff to know if it's TPA/Cash
 *   • Doctor            — admitting consultant
 *   • Mobile            — primary contact / next-of-kin lookup
 *
 * URL: `/print/ipd-sticker/<admissionId>?copies=<n>&autoprint=0`
 * Doctor name is fetched from the admission record (`admitting_doctor_name`),
 * falls back to "—".
 */
function Sticker({ admission, patient, hospital, branch, dateStr, timeStr }) {
  const ageSex = [patient?.age != null ? `${patient.age}y` : "", (patient?.gender || "")[0]?.toUpperCase()]
    .filter(Boolean).join(" / ");
  const wardBed = [admission?.ward, admission?.bed_no].filter(Boolean).join(" · ") || "—";
  const payer = admission?.panel_name
    ? `${(admission.payer_category || "panel").toUpperCase()} · ${admission.panel_name}`
    : (admission?.payer_category || "cash").toUpperCase();

  return (
    <div className="ipd-sticker">
      <div className="ipd-sticker-head">
        <div className="ipd-sticker-brand">{(hospital?.name || branch?.name || "CareO Eye").toUpperCase()}</div>
        <div className="ipd-sticker-type">IPD</div>
      </div>
      <div className="ipd-sticker-row">
        <span className="ipd-sticker-key">IPD No</span>
        <span className="ipd-sticker-val ipd-sticker-ipdno">{admission?.ipd_no || "—"}</span>
      </div>
      <div className="ipd-sticker-row ipd-sticker-row-half">
        <div><span className="ipd-sticker-key">UHID</span><span className="ipd-sticker-val">{patient?.uhid || "—"}</span></div>
        <div><span className="ipd-sticker-key">Age/Sex</span><span className="ipd-sticker-val">{ageSex || "—"}</span></div>
      </div>
      <div className="ipd-sticker-row">
        <span className="ipd-sticker-key">Name</span>
        <span className="ipd-sticker-val">{patient?.name || "—"}</span>
      </div>
      <div className="ipd-sticker-row ipd-sticker-row-half">
        <div><span className="ipd-sticker-key">Admit</span><span className="ipd-sticker-val">{dateStr} {timeStr}</span></div>
      </div>
      <div className="ipd-sticker-row ipd-sticker-row-half">
        <div><span className="ipd-sticker-key">Ward/Bed</span><span className="ipd-sticker-val">{wardBed}</span></div>
        <div><span className="ipd-sticker-key">Mob</span><span className="ipd-sticker-val">{patient?.mobile || "—"}</span></div>
      </div>
      <div className="ipd-sticker-row">
        <span className="ipd-sticker-key">Doctor</span>
        <span className="ipd-sticker-val">{admission?.admitting_doctor_name || "—"}</span>
      </div>
      <div className="ipd-sticker-row">
        <span className="ipd-sticker-key">Payer</span>
        <span className="ipd-sticker-val">{payer}</span>
      </div>
    </div>
  );
}

export default function PrintIpdSticker() {
  const { admissionId } = useParams();
  const [search] = useSearchParams();
  const copies = Math.max(1, Math.min(4, Number(search.get("copies")) || 1));
  const autoprint = search.get("autoprint") !== "0";

  const [admission, setAdmission] = useState(null);
  const [patient, setPatient] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);

  useEffect(() => {
    (async () => {
      const me = await api.get("/auth/me");
      setHospital(me.data.hospital);
      setBranch(me.data.branch);
      const { data } = await api.get(`/ipd/admissions/${admissionId}`);
      // Single-admission endpoint wraps the doc under `admission` and ships
      // nursing notes / discharge alongside. We only need the admission slice.
      const adm = data?.admission || data;
      // Resolve the admitting doctor's display name if the admission only
      // carries an ID (older records). Falls back gracefully if not found.
      if (!adm.admitting_doctor_name && adm.admitting_doctor_id) {
        try {
          const docs = await api.get("/hospital/doctors");
          const d = (docs.data.items || []).find(x => x.id === adm.admitting_doctor_id);
          if (d) adm.admitting_doctor_name = `Dr. ${d.name}`;
        } catch { /* non-fatal */ }
      }
      setAdmission(adm);
      if (adm?.patient_id) {
        const p = await api.get(`/patients/${adm.patient_id}`);
        setPatient(p.data.patient || p.data);
      }
      if (autoprint) setTimeout(() => window.print(), 500);
    })();
  }, [admissionId, autoprint]);

  if (!admission || !patient) {
    return <div className="p-8 text-sm text-ink-muted">Loading sticker…</div>;
  }

  const ts = admission.admitted_at ? new Date(admission.admitted_at) : new Date();
  const dateStr = ts.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const timeStr = ts.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });

  return (
    <>
      <style>{`
        @page { size: 100mm 50mm; margin: 0; }
        @media print {
          body { background: white; }
          .no-print { display: none !important; }
        }
        body { background: #f5f5f5; }
        .ipd-sticker {
          width: 100mm;
          height: 50mm;
          padding: 2.5mm 4mm;
          box-sizing: border-box;
          font-family: 'Calibri', 'Carlito', 'Helvetica', 'Arial', sans-serif;
          color: #111;
          page-break-after: always;
          background: white;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .ipd-sticker-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1.2px solid #000;
          padding-bottom: 1mm;
          margin-bottom: 1mm;
        }
        .ipd-sticker-brand {
          font-weight: 700;
          font-size: 9pt;
          letter-spacing: 0.3px;
        }
        .ipd-sticker-type {
          font-weight: 800;
          font-size: 9pt;
          background: #b91c1c;
          color: #fff;
          padding: 0.4mm 2.5mm;
          border-radius: 1.5mm;
        }
        .ipd-sticker-row {
          display: flex;
          align-items: baseline;
          gap: 2mm;
          font-size: 8pt;
          line-height: 1.1;
          margin: 0.2mm 0;
        }
        .ipd-sticker-row-half { gap: 5mm; }
        .ipd-sticker-row-half > div { display: flex; align-items: baseline; gap: 1.5mm; }
        .ipd-sticker-key {
          font-size: 6pt;
          font-weight: 700;
          color: #555;
          letter-spacing: 0.4px;
          text-transform: uppercase;
          min-width: 12mm;
        }
        .ipd-sticker-val {
          font-weight: 600;
          font-size: 8.5pt;
        }
        .ipd-sticker-ipdno {
          font-family: 'Courier New', monospace;
          font-size: 10pt;
          font-weight: 800;
          color: #b91c1c;
        }
      `}</style>
      {Array.from({ length: copies }).map((_, i) =>
        <Sticker key={i} admission={admission} patient={patient} hospital={hospital} branch={branch} dateStr={dateStr} timeStr={timeStr} />
      )}
      <div className="no-print" style={{ position: "fixed", top: 8, right: 8 }}>
        <button onClick={() => window.print()} style={{ padding: "6px 12px", background: "#111", color: "#fff", borderRadius: 6, fontSize: 12, border: 0, cursor: "pointer" }} data-testid="ipd-sticker-print-btn">
          Print sticker
        </button>
      </div>
    </>
  );
}
